/**
 * Persisted user settings.
 *
 * Stored as one versioned JSON blob rather than a key per value: the settings
 * are read together at startup, and a single `version` field makes it possible
 * to migrate — or discard — an old shape deliberately instead of silently
 * loading half-updated state.
 */

import { toNotation, toTransposition, type Notation } from '../music/notation'
import { DEFAULT_TUNING, tuningById } from '../music/tunings'
import { clamp } from '../lib/math'
import { clearSetting, readSetting, writeSetting } from '../lib/storage'
import { toLayout, type BarLayout } from '../ui/tuner/layout'

/** localStorage key holding the whole settings blob. */
export const SETTINGS_KEY = 'multitimbral.settings'

/** Bump when a field changes meaning; older blobs then fall back to defaults. */
export const SETTINGS_VERSION = 1

/** Concert pitch bounds and step, in Hz. */
export const MIN_A4 = 428
export const MAX_A4 = 454
export const A4_STEP = 0.5

/** Everything the app remembers between visits. */
export interface Settings {
  readonly tuningId: string
  /** Concert pitch in Hz, on a 0.5 Hz grid. */
  readonly a4: number
  readonly notation: Notation
  /** Display shift in semitones for transposing instruments. */
  readonly transposition: number
  readonly layout: BarLayout
  /** Preferred audio input, or null to let the browser choose. */
  readonly deviceId: string | null
}

/** What a first-time visitor gets. */
export const DEFAULT_SETTINGS: Settings = {
  tuningId: DEFAULT_TUNING.id,
  a4: 440,
  notation: 'english',
  transposition: 0,
  layout: 'stacked',
  deviceId: null,
}

/** Snap a concert pitch onto the supported grid and range. */
export function normaliseA4(value: number): number {
  if (!Number.isFinite(value)) return DEFAULT_SETTINGS.a4
  const snapped = Math.round(value / A4_STEP) * A4_STEP
  return clamp(snapped, MIN_A4, MAX_A4)
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

/**
 * Coerce anything read from storage into valid settings.
 *
 * Every field is validated on its own, so a blob that is partly corrupt still
 * yields the settings it does carry rather than being thrown away wholesale.
 * A version mismatch is the one case that discards everything.
 */
export function parseSettings(raw: string | null): Settings {
  if (raw === null) return DEFAULT_SETTINGS

  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return DEFAULT_SETTINGS
  }
  if (!isRecord(parsed)) return DEFAULT_SETTINGS
  if (parsed.version !== SETTINGS_VERSION) return DEFAULT_SETTINGS

  const deviceId =
    typeof parsed.deviceId === 'string' && parsed.deviceId !== '' ? parsed.deviceId : null

  return {
    tuningId: tuningById(typeof parsed.tuningId === 'string' ? parsed.tuningId : null).id,
    a4: typeof parsed.a4 === 'number' ? normaliseA4(parsed.a4) : DEFAULT_SETTINGS.a4,
    notation: toNotation(typeof parsed.notation === 'string' ? parsed.notation : null),
    transposition: toTransposition(
      typeof parsed.transposition === 'number' ? parsed.transposition : null,
    ),
    layout: toLayout(typeof parsed.layout === 'string' ? parsed.layout : null),
    deviceId,
  }
}

/**
 * Key the settings lived under before the app was renamed.
 *
 * Read once as a fallback so a player who had the tuner set up does not come
 * back to defaults. Nothing writes it any more, and it is cleared as soon as
 * the settings have been carried over.
 */
export const LEGACY_SETTINGS_KEY = 'polytuner.settings'

/** Read the stored settings, or the defaults. */
export function loadSettings(): Settings {
  const stored = readSetting(SETTINGS_KEY)
  if (stored !== null) return parseSettings(stored)

  const legacy = readSetting(LEGACY_SETTINGS_KEY)
  if (legacy === null) return parseSettings(null)

  const settings = parseSettings(legacy)
  if (saveSettings(settings)) clearSetting(LEGACY_SETTINGS_KEY)
  return settings
}

/** Persist settings. Returns false when storage refused the write. */
export function saveSettings(settings: Settings): boolean {
  return writeSetting(SETTINGS_KEY, JSON.stringify({ version: SETTINGS_VERSION, ...settings }))
}
